// src/utils/eventBarcodeSheet.js
// "Gate sign-out" sheet: one Code128 barcode (the student's SIS ID) per
// confirmed student, for the SOC to scan students out at the gate.
// Imperative jsPDF (project convention — no html2canvas / doc.html()).
//
// Requires: npm install jsbarcode
//
// Two exports:
//   downloadEventBarcodeSheet(event, rows)      → save the PDF locally
//   eventBarcodeSheetBase64(event, rows)         → return the PDF as base64
//                                                  (for emailing via a function)
//
// rows: [{ name, sisId }] — students only. A row with no sisId is listed
// without a barcode so the SOC can sign that student out by hand.

import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";

const NAVY = [35, 60, 95];
const RED = [232, 53, 48];

function fmt(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("en-US", {
      timeZone: "Asia/Amman", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
}

// Render a Code128 barcode to a PNG data URL via an offscreen canvas.
function barcodeDataUrl(value) {
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(value), {
      format: "CODE128",
      displayValue: true,
      fontSize: 14,
      height: 48,
      margin: 0,
    });
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

// Build the jsPDF doc (shared by download + base64 paths).
function buildBarcodeDoc(event, rows) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  // Header band
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 64, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("KING'S ACADEMY - GATE SIGN-OUT", margin, 26);
  doc.setFontSize(15);
  doc.text(event.name || "Event", margin, 48);

  doc.setTextColor(90, 90, 90);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Depart: ${fmt(event.startAt)}    ·    ${rows.length} student${rows.length === 1 ? "" : "s"}`, margin, 82);

  if (rows.length === 0) {
    doc.setTextColor(120, 120, 120);
    doc.setFontSize(12);
    doc.text("No confirmed students on this trip yet.", margin, 120);
  }

  // Two-column grid of barcode cards.
  const cols = 2;
  const gutter = 18;
  const cellW = (pageW - margin * 2 - gutter * (cols - 1)) / cols;
  const cellH = 92;
  let x = margin;
  let y = 98;
  let col = 0;

  rows.forEach((r) => {
    if (y + cellH > pageH - margin) { doc.addPage(); x = margin; y = margin; col = 0; }

    doc.setDrawColor(220);
    doc.roundedRect(x, y, cellW, cellH, 6, 6);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...NAVY);
    doc.text(String(r.name || "").slice(0, 34), x + 10, y + 20);

    if (r.sisId) {
      const url = barcodeDataUrl(r.sisId);
      if (url) {
        try { doc.addImage(url, "PNG", x + 10, y + 28, cellW - 20, 52); } catch { /* ignore */ }
      } else {
        doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(120, 120, 120);
        doc.text(`ID ${r.sisId} (barcode failed)`, x + 10, y + 52);
      }
    } else {
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...RED);
      doc.text("No ID on file — sign out by hand", x + 10, y + 52);
    }

    col += 1;
    if (col >= cols) { col = 0; x = margin; y += cellH + 12; }
    else { x += cellW + gutter; }
  });

  return doc;
}

export function downloadEventBarcodeSheet(event, rows) {
  const doc = buildBarcodeDoc(event, rows);
  const safe = (event.name || "event").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
  doc.save(`gate-signout-${safe || "event"}.pdf`);
}

// Returns the PDF as a raw base64 string (no data: prefix), for upload/email.
export function eventBarcodeSheetBase64(event, rows) {
  const doc = buildBarcodeDoc(event, rows);
  const dataUri = doc.output("datauristring");
  const marker = "base64,";
  const idx = dataUri.indexOf(marker);
  return idx >= 0 ? dataUri.substring(idx + marker.length) : "";
}